import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import { DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { apiget, deleteManyApi } from '../../service/api';
import DeleteModel from '../../components/Deletemodle';
import TableStyle from '../../components/TableStyle';
import { CustomTabPanel, a11yProps } from '../../components/CustomTabPanel';
import { connectSocket } from '../../service/socket';
import {
  BANKRATE_STATUSES,
  LOAN_TYPES,
  statusColor,
  fmtCurrency,
} from './statusConfig';
import AddBankrateLead from './Add';
import ImportCsvModal from './ImportCsvModal';
import RoundRobinSettings from './RoundRobinSettings';
import AcceptingLeadsSwitch from './AcceptingLeadsSwitch';

// Roles that can be assigned Bankrate leads via round-robin — these users
// see the self-serve "Accepting new leads" switch.
const LEAD_RECEIVING_ROLES = ['agent', 'manager', 'loanOfficer'];
// Roles that can see/edit the Round Robin admin tab.
const ADMIN_ROLES = ['admin', 'manager'];

// Initial-contact SLA in milliseconds. Mirrors RoundRobinSettings.sharkTankMinutes
// default (5). The number isn't fetched per-render — when an admin tunes it the
// next page load picks it up. Display-only; the server is authoritative.
const SLA_MS = 5 * 60 * 1000;

function CountdownCell({ row }) {
  // Shark tank trumps the countdown — nothing left to count.
  if (row.sharkTank) {
    return <Chip size="small" label="SHARK TANK" color="error" />;
  }
  // Lead is unassigned (status:"New", needs manual assignment by admin).
  if (!row.assignedAt || row.status !== 'New') {
    return <Box sx={{ color: 'text.disabled' }}>—</Box>;
  }
  const assignedMs = new Date(row.assignedAt).getTime();
  const remaining = Math.max(0, assignedMs + SLA_MS - Date.now());
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  const label = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  let color = 'success';
  if (remaining === 0) color = 'error';
  else if (remaining < 2 * 60 * 1000) color = 'warning';
  return <Chip size="small" label={label} color={color} variant="outlined" />;
}

function CustomToolbar({ selectedRowIds, fetchdata }) {
  const [opendelete, setOpendelete] = useState(false);

  const handleCloseDelete = () => setOpendelete(false);
  const handleOpenDelete = () => setOpendelete(true);

  const deleteManyBankrate = async (ids) => {
    await deleteManyApi('bankrate-lead/deletemany', ids);
    fetchdata();
    handleCloseDelete();
  };

  return (
    <GridToolbarContainer>
      <GridToolbar />
      {selectedRowIds && selectedRowIds.length > 0 && (
        <Button
          variant="text"
          sx={{ textTransform: 'capitalize' }}
          startIcon={<DeleteOutline />}
          onClick={handleOpenDelete}
        >
          Delete
        </Button>
      )}
      <DeleteModel
        opendelete={opendelete}
        handleClosedelete={handleCloseDelete}
        deletedata={deleteManyBankrate}
        id={selectedRowIds}
      />
    </GridToolbarContainer>
  );
}

const BankrateLead = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [userAction, setUserAction] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const userRole = (localStorage.getItem('userRole') || '').trim();
  const canReceiveLeads = LEAD_RECEIVING_ROLES.includes(userRole);
  const isAdmin = ADMIN_ROLES.includes(userRole);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [loanTypeFilter, setLoanTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  // Scope: 'mine' (own + state-matched shark tank) or 'all'.
  // LO default = mine; admin default = all. Either can flip the toggle.
  const [scope, setScope] = useState(canReceiveLeads && !isAdmin ? 'mine' : 'all');

  // 1-second tick used by the Countdown column. One interval drives all rows.
  const [, setTick] = useState(0);
  const tickRef = useRef(null);

  // Modals
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);

  const fetchdata = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (loanTypeFilter) params.append('loanType', loanTypeFilter);
    if (searchText) params.append('q', searchText);
    if (scope) params.append('scope', scope);

    const qs = params.toString();
    const result = await apiget(`bankrate-lead/list${qs ? `?${qs}` : ''}`);
    if (result?.status === 200) {
      setData(result.data?.result || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAction]);

  // Re-fetch when server-side filters or scope change.
  useEffect(() => {
    const t = setTimeout(fetchdata, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, loanTypeFilter, searchText, scope]);

  // Countdown driver — single 1s tick re-renders all visible rows.
  useEffect(() => {
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  // Live refresh on socket events: when a lead is assigned to me, or any
  // lead in a state I'm licensed for enters the shark tank, refetch so
  // the list and shark-tank styling reflect server truth.
  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;
    const refetch = () => fetchdata();
    socket.on('leadAssigned', refetch);
    socket.on('sharkTankLead', refetch);
    return () => {
      socket.off('leadAssigned', refetch);
      socket.off('sharkTankLead', refetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: 'Name',
        flex: 1.2,
        renderCell: (params) => {
          const name =
            params.row.fullName ||
            [params.row.firstName, params.row.lastName].filter(Boolean).join(' ') ||
            '—';
          return (
            <Box
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
              onClick={() => navigate(`/dashboard/bankrate-lead/view/${params.row._id}`)}
            >
              {name}
            </Box>
          );
        },
      },
      { field: 'email', headerName: 'Email', flex: 1.3 },
      { field: 'phone', headerName: 'Phone', flex: 1 },
      { field: 'loanType', headerName: 'Type', flex: 1 },
      {
        field: 'loanAmount',
        headerName: 'Loan Amount',
        flex: 1,
        valueFormatter: (p) => fmtCurrency(p.value),
      },
      { field: 'zipCode', headerName: 'ZIP', width: 90 },
      {
        field: 'creditProfileAmount',
        headerName: 'Credit',
        width: 90,
        valueFormatter: (p) => (p.value ? String(p.value) : '—'),
      },
      {
        field: 'countdown',
        headerName: 'Countdown',
        width: 130,
        sortable: false,
        renderCell: (p) => <CountdownCell row={p.row} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.value || 'New'}
            color={statusColor(p.value)}
            variant="outlined"
          />
        ),
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        flex: 1.1,
        valueGetter: (p) => {
          const a = p.row.assignedTo;
          if (!a) return '—';
          if (typeof a === 'string') return a;
          return [a.firstName, a.lastName].filter(Boolean).join(' ') || a.emailAddress || '—';
        },
      },
      {
        field: 'leadcost',
        headerName: 'Lead Cost',
        width: 110,
        valueFormatter: (p) => fmtCurrency(p.value),
      },
      {
        field: 'createdOn',
        headerName: 'Created',
        width: 120,
        valueFormatter: (p) => (p.value ? dayjs(p.value).format('MM/DD/YYYY') : '—'),
      },
    ],
    [navigate]
  );

  return (
    <Container maxWidth="xl">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
        mb={2}
      >
        <Typography variant="h4">Bankrate Leads</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {canReceiveLeads && <AcceptingLeadsSwitch />}
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setOpenImport(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAdd(true)}
          >
            New Lead
          </Button>
        </Stack>
      </Stack>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label="Leads" {...a11yProps(0)} />
        {isAdmin && <Tab label="Round Robin" {...a11yProps(1)} />}
      </Tabs>

      <CustomTabPanel value={tabValue} index={0}>
        <Card sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Search name / email / phone / leadId"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ minWidth: 320 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {BANKRATE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Loan Type</InputLabel>
              <Select
                label="Loan Type"
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {LOAN_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={scope === 'mine'}
                  onChange={(e) => setScope(e.target.checked ? 'mine' : 'all')}
                />
              }
              label="Mine only"
            />
          </Stack>
        </Card>

        <TableStyle>
          <Box width="100%">
            <Card sx={{ height: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  autoHeight
                  rows={data}
                  columns={columns}
                  getRowId={(row) => row._id}
                  getRowClassName={(p) =>
                    p.row.sharkTank ? 'bankrate-shark-tank-row' : ''
                  }
                  checkboxSelection
                  disableSelectionOnClick
                  onSelectionModelChange={(ids) => setSelectedRowIds(ids)}
                  components={{ Toolbar: CustomToolbar }}
                  componentsProps={{ toolbar: { selectedRowIds, fetchdata } }}
                  pageSize={25}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    '& .bankrate-shark-tank-row': {
                      borderLeft: (theme) => `4px solid ${theme.palette.error.main}`,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(244,67,54,0.08)'
                          : 'rgba(244,67,54,0.04)',
                    },
                  }}
                />
              )}
            </Card>
          </Box>
        </TableStyle>
      </CustomTabPanel>

      {isAdmin && (
        <CustomTabPanel value={tabValue} index={1}>
          <RoundRobinSettings />
        </CustomTabPanel>
      )}

      <AddBankrateLead
        open={openAdd}
        handleClose={() => setOpenAdd(false)}
        setUserAction={setUserAction}
      />
      <ImportCsvModal
        open={openImport}
        handleClose={() => setOpenImport(false)}
        onImported={fetchdata}
      />
    </Container>
  );
};

export default BankrateLead;
